import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'
import {
  useGetQuestionnaireByVisitQuery,
  useGetQuestionnaireQuery,
  usePostQuestionnaireMutation,
  usePutQuestionnaireByVisitMutation,
} from '@/entities/questionnaire/api'
import {
  clearSurveyDraft,
  readSurveyDraft,
  surveyDraftKey,
  writeSurveyDraft,
} from './survey-draft'
import type {
  IQuestion,
  IQuestionnaireAnswer,
  IQuestionnaireVisitAnswer,
  IVisitQuestionnaireItem,
} from '@/entities/questionnaire/types'

export type SurveyFormValues = Record<string, string | boolean>

/**
 * The last-donation date question: the backend returns the donor's known last
 * donation date on it (when the questionnaire is fetched with a `donor_id`) as
 * a suggestion to prefill the input with. It is submitted as an ordinary
 * `answer_date`, so nothing else in the flow treats it specially.
 */
const isLastDonationQuestion = (question: IQuestion) =>
  question.categoryKey === 'DONATION_HISTORY' &&
  question.questionType === 'DATE'

export function useMedicalSurveyForm(
  visitId: string,
  donorId?: string,
  donorGender?: 'MALE' | 'FEMALE',
  /**
   * Donor workflow step. When null/undefined the survey has not been reached
   * yet, so we skip fetching the per-visit answers (nothing is stored yet).
   */
  step?: string | null,
  onSuccess?: () => void,
) {
  const { data: questionnaireData, isPending: questionsLoading } =
    useGetQuestionnaireQuery(donorGender, donorId)

  // Existing per-visit answers — only meaningful once the workflow has started.
  const { data: visitData, isPending: visitAnswersLoading } =
    useGetQuestionnaireByVisitQuery(visitId, { enabled: !!step })

  const { mutate: create, status: createStatus } =
    usePostQuestionnaireMutation(donorId)
  const { mutate: update, status: updateStatus } =
    usePutQuestionnaireByVisitMutation(visitId, donorId)

  const questions = useMemo(
    () => questionnaireData?.data ?? [],
    [questionnaireData],
  )

  const visitAnswers = useMemo<IVisitQuestionnaireItem[]>(
    () => visitData?.data ?? [],
    [visitData],
  )

  // The survey is "completed" when at least one answer has been persisted.
  const isCompleted = useMemo(
    () => !!step && visitAnswers.some((a) => a.answeredAt),
    [step, visitAnswers],
  )

  // Read-only until the user chooses to edit an already-completed survey.
  const [isEditing, setIsEditing] = useState(false)

  const form = useForm<SurveyFormValues>({
    defaultValues: { confirmed: false },
  })

  // Unfinished answers are kept per donor card + visit, see `survey-draft.ts`.
  const draftKey = surveyDraftKey(donorId, visitId)

  useEffect(() => {
    if (questions.length === 0) return

    const answerByQuestion = new Map(visitAnswers.map((a) => [a.questionId, a]))

    const defaults: SurveyFormValues = { confirmed: isCompleted }
    questions.forEach((q) => {
      const stored = answerByQuestion.get(q.questionId)
      if (!stored) {
        // Unanswered: prefill the last-donation date when the backend knows it.
        defaults[q.questionId] = isLastDonationQuestion(q)
          ? (q.lastDonationDate ?? '')
          : ''
        return
      }

      if (q.questionType === 'BOOLEAN') {
        defaults[q.questionId] =
          stored.answerBoolean === null ? '' : String(stored.answerBoolean)
      } else if (q.questionType === 'DATE') {
        defaults[q.questionId] = stored.answerDate ?? stored.answerDetails ?? ''
      } else {
        defaults[q.questionId] = stored.answerDetails ?? ''
      }
    })

    if (isCompleted) {
      // The answers live on the server now — any leftover draft is obsolete.
      clearSurveyDraft(draftKey)
      form.reset(defaults)
      return
    }

    // Restore whatever the doctor had already filled in before leaving.
    const draft = readSurveyDraft(draftKey)
    if (draft) {
      Object.entries(draft.answers).forEach(([questionId, value]) => {
        if (questionId in defaults) defaults[questionId] = value
      })
    }

    form.reset(defaults)
  }, [questions, visitAnswers, isCompleted, draftKey, form])

  // Persist every change until the survey is submitted.
  useEffect(() => {
    if (!draftKey || isCompleted) return

    const subscription = form.watch((values) => {
      writeSurveyDraft(draftKey, { answers: values as SurveyFormValues })
    })

    return () => subscription.unsubscribe()
  }, [draftKey, isCompleted, form])

  const onSubmit = form.handleSubmit(async (data) => {
    const { confirmed: _confirmed, ...answerData } = data

    if (isCompleted) {
      // Editing an already-completed survey → PUT per-visit answers.
      const answers: IQuestionnaireVisitAnswer[] = questions.map((q) => {
        const val = answerData[q.questionId]
        const answer: IQuestionnaireVisitAnswer = { question_id: q.questionId }

        if (q.questionType === 'BOOLEAN') {
          answer.answer_boolean = val === 'true'
        } else if (q.questionType === 'DATE') {
          answer.answer_date = val as string
        } else {
          answer.answer_details = val as string
        }

        return answer
      })

      update(
        { answers },
        {
          onSuccess: () => {
            setIsEditing(false)
            onSuccess?.()
          },
        },
      )
      return
    }

    // First submission → POST /submit.
    const answers: IQuestionnaireAnswer[] = questions.map((q) => {
      const val = answerData[q.questionId]
      const answer: IQuestionnaireAnswer = { question_id: q.questionId }

      if (q.questionType === 'BOOLEAN') {
        answer.answer_boolean = val === 'true'
      } else if (q.questionType === 'DATE') {
        answer.answer_date = val as string
      } else {
        answer.answer_text = val as string
      }

      return answer
    })

    create(
      {
        visitId,
        donorGender: donorGender ?? '',
        answers,
      },
      {
        onSuccess: () => {
          form.reset()
          // After the reset, so the watcher can't write the draft back.
          clearSurveyDraft(draftKey)
          onSuccess?.()
        },
        onError: () => {
          form.reset()
        },
      },
    )
  })

  const status = isCompleted ? updateStatus : createStatus

  return {
    form,
    questions,
    questionsLoading: questionsLoading || (!!step && visitAnswersLoading),
    status,
    onSubmit,
    isCompleted,
    isEditing,
    setIsEditing,
    draftKey,
  }
}
